<script setup lang="ts">
/**
 * Accounts, and the six things an operator does to one.
 *
 * ── Destructive actions ask ──────────────────────────────────────────────────
 *
 * Ban and credit both take a typed reason, and both go through `confirm`. Not
 * ceremony: this is a table of rows that look alike, the buttons are 30px
 * apart, and banning the account below the one you meant is a mistake with a
 * person on the other end of it. A reason is also what the audit trail records.
 *
 * **Delete is deliberately not offered**, though the endpoint exists. Deleting
 * an account from a list is the single most destructive thing on this screen
 * and the one most likely to be a mis-click; if it is ever wanted it should be
 * on a per-account detail view behind a typed confirmation, not a button in a
 * row.
 *
 * ── Reset password sends a link; it does not set one ─────────────────────────
 *
 * `POST :id/password` exists and sets a password directly, and this screen does
 * not use it. That route means the operator chooses the password, knows it, and
 * has to get it to the player over some channel — which is a credential in a
 * chat log. *Reset password* here triggers the ordinary one-hour, single-use
 * email link at the account's own confirmed address, so nobody but the person
 * reading that inbox can complete it. For "I have lost access", which is what
 * this is for, that is both easier and safer.
 */
import { ref, watch } from 'vue';

import { adminApi, type AdminUser } from './admin-api';
import CreateUserModal from './CreateUserModal.vue';
import { money, usePane, when } from './pane';

/**
 * The four KYC states, as an operator reads them rather than as the enum
 * spells them. Shared verbatim (by hand, not by import) with
 * `CreateUserModal.vue`'s own KYC field — the same small duplication the
 * Social-link platform list already accepts elsewhere in this panel.
 */
const KYC_OPTIONS: Array<{ value: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'; label: string }> = [
  { value: 'NONE', label: 'None' },
  { value: 'PENDING', label: 'Requested' },
  { value: 'APPROVED', label: 'Accepted' },
  { value: 'REJECTED', label: 'Revoked' },
];

const page = ref(1);
const search = ref('');
const status = ref('');
const kycFilter = ref('');
const showCreate = ref(false);

const players = usePane(() =>
  adminApi.players({
    page: page.value,
    search: search.value,
    status: status.value,
    kycStatus: kycFilter.value,
  }),
);

// Any change to the query goes back to page one — page 4 of a filtered list is
// almost never the page the previous filter's page 4 was.
watch([search, status, kycFilter], () => {
  page.value = 1;
  void players.reload();
});
watch(page, () => void players.reload());

function onUserCreated(): void {
  showCreate.value = false;
  void players.reload();
}

function ban(id: string, name: string): void {
  const reason = window.prompt(`Ban ${name}. Reason (recorded in the audit trail):`);
  if (reason === null || reason.trim() === '') return;
  void players.act(() => adminApi.ban(id, reason.trim()));
}

/**
 * Grants currency.
 *
 * **Adds only.** `AddCreditsDto` is `@IsInt() @IsPositive()`, so the endpoint
 * has no deduction path at all — offering a negative here would be a form that
 * 400s. Taking currency away is a ledger correction and wants its own route
 * with its own audit trail, not a minus sign in a prompt.
 *
 * **Locked to one currency each**, on two separate buttons/columns, rather
 * than one control that asks which. Asking used to be how this worked, and
 * handing somebody diamonds by accident on a mis-typed answer is not
 * recoverable through this screen — a dedicated "Add Diamonds" column exists
 * for that side; this one only ever grants credits.
 */
function addCredits(id: string, name: string): void {
  const raw = window.prompt(`How many credits for ${name}? (whole number)`);
  if (raw === null) return;
  const amount = Math.floor(Number(raw));
  if (!Number.isFinite(amount) || amount <= 0) return;

  const description = window.prompt('Note (recorded in the ledger):') ?? '';
  if (description.trim() === '') return;
  void players.act(() => adminApi.addCredits(id, amount, 'SOFT', description.trim()));
}

/** The diamonds counterpart to `addCredits` above — same shape, `PREMIUM` only. */
function addDiamonds(id: string, name: string): void {
  const raw = window.prompt(`How many diamonds for ${name}? (whole number)`);
  if (raw === null) return;
  const amount = Math.floor(Number(raw));
  if (!Number.isFinite(amount) || amount <= 0) return;

  const description = window.prompt('Note (recorded in the ledger):') ?? '';
  if (description.trim() === '') return;
  void players.act(() => adminApi.addCredits(id, amount, 'PREMIUM', description.trim()));
}

/** What the last action reported, shown under the toolbar and then forgotten. */
const said = ref<string | null>(null);
function flash(message: string): void {
  said.value = message;
  window.setTimeout(() => (said.value = null), 4000);
}

/**
 * Sends the reset link.
 *
 * ── The unconfirmed case is called out, because it is the likely one ─────────
 *
 * `AuthService.resetPassword` does not touch `emailVerified` — it sets the
 * password, burns the token and drops every refresh token, and that is all. So
 * on an account that never confirmed its address, the reset genuinely works and
 * the player is *still* locked out, because `EmailVerifiedGuard` and `App.vue`'s
 * `unverified` phase are what is actually stopping them.
 *
 * That is not a hole — the link went to the address, so completing it proves the
 * same inbox control confirmation proves — but it is the wrong tool for the
 * symptom, and "I reset it and they still cannot get in" is a support ticket
 * that costs an hour. The account list already shows the Unconfirmed chip; this
 * says what to press instead.
 */
function resetPassword(id: string, who: string, confirmed: boolean): void {
  const warning = confirmed
    ? ''
    : `\n\nNote: this account has never confirmed its email. A reset will set a ` +
      `password but will NOT let them in — the confirmation gate is what is ` +
      `blocking them. "Confirm email" is probably what you want.`;
  if (!window.confirm(`Send ${who} a password-reset email? Any earlier link they have stops working.${warning}`)) return;
  void players.act(async () => {
    const r = await adminApi.sendPasswordReset(id);
    flash(`Reset link sent to ${r.sentTo}.`);
  });
}

/**
 * Sets KYC status directly, from the table row.
 *
 * Replaces the old "Verify KYC" button, which could only ever jump straight
 * to APPROVED. The endpoint it called (`POST :id/verify-kyc`) is untouched and
 * simply goes unused now — the four-state dropdown covers what it did and the
 * other three transitions besides.
 */
function changeKyc(u: AdminUser, event: Event): void {
  const value = (event.target as HTMLSelectElement).value as 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  if (value === u.kycStatus) return;
  void players.act(() => adminApi.setKycStatus(u.id, value));
}

const pages = () => {
  const d = players.data.value;
  return d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
};
</script>

<template>
  <h1>Players</h1>
  <p class="lede">Every account on this server.</p>

  <div class="toolbar">
    <input
      v-model="search"
      type="search"
      placeholder="Search email or call sign"
      data-test="admin-user-search"
    />
    <select v-model="status" data-test="admin-user-status" aria-label="State">
      <option value="">All</option>
      <option value="active">Active</option>
      <option value="banned">Banned</option>
    </select>
    <select v-model="kycFilter" data-test="admin-user-kyc-filter" aria-label="KYC">
      <option value="">All</option>
      <option v-for="o in KYC_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
    </select>
    <button :disabled="players.loading.value" @click="players.reload()">Refresh</button>
    <button class="accent" @click="showCreate = true">+ Add user</button>
  </div>

  <p v-if="players.error.value" class="bad">{{ players.error.value }}</p>
  <p v-if="said" class="note" data-test="admin-players-said">{{ said }}</p>

  <div class="scroller">
    <table data-test="admin-users-table">
      <thead>
        <tr>
          <th>Call sign</th>
          <th>Email</th>
          <th>State</th>
          <th>KYC</th>
          <th>Change KYC</th>
          <th>Credits</th>
          <th>Diamonds</th>
          <th>Add Diamonds</th>
          <th>Enlisted</th>
          <th>Last seen</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="u in players.data.value?.items ?? []" :key="u.id">
          <td>{{ u.username ?? '—' }}</td>
          <td>{{ u.email }}</td>
          <td>
            <span v-if="u.banned" class="chip bad">Banned</span>
            <span v-else class="chip ok">Active</span>
            <span v-if="!u.emailVerified" class="chip warn">Unconfirmed</span>
            <span v-if="u.role !== 'PLAYER'" class="chip">{{ u.role }}</span>
          </td>
          <td>
            <span
              class="chip"
              :class="u.kycStatus === 'APPROVED' ? 'ok' : u.kycStatus === 'REJECTED' ? 'bad' : 'warn'"
            >{{ u.kycStatus }}</span>
          </td>
          <td>
            <select :value="u.kycStatus" data-test="admin-user-kyc-change" @change="changeKyc(u, $event)">
              <option v-for="o in KYC_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </td>
          <td>{{ money(u.softBalance) }}</td>
          <td>{{ u.premiumBalance }}</td>
          <td>
            <button @click="addDiamonds(u.id, u.username ?? u.email)">Add Diamonds</button>
          </td>
          <td>{{ when(u.createdAt) }}</td>
          <td>{{ when(u.lastSeenAt) }}</td>
          <td class="actions">
            <button v-if="!u.banned" class="danger" @click="ban(u.id, u.username ?? u.email)">
              Ban
            </button>
            <button v-else @click="players.act(() => adminApi.unban(u.id))">Unban</button>
            <button
              v-if="!u.emailVerified"
              @click="players.act(() => adminApi.verifyEmail(u.id))"
            >
              Confirm email
            </button>
            <button @click="resetPassword(u.id, u.username ?? u.email, u.emailVerified)">
              Reset password
            </button>
            <button @click="addCredits(u.id, u.username ?? u.email)">Add Credits</button>
          </td>
        </tr>
        <tr v-if="!players.loading.value && (players.data.value?.items.length ?? 0) === 0">
          <td colspan="11" class="empty">No accounts match that.</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="pager">
    <button :disabled="page <= 1 || players.loading.value" @click="page -= 1">Newer</button>
    <span>Page {{ page }} of {{ pages() }} · {{ players.data.value?.total ?? 0 }} accounts</span>
    <button :disabled="page >= pages() || players.loading.value" @click="page += 1">Older</button>
  </div>

  <CreateUserModal v-if="showCreate" @close="showCreate = false" @created="onUserCreated" />
</template>
